import { Channel, useDeleteChannelMutation } from 'api/ChannelsAPI';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import { BUTTON_SIZES, DestructiveButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

export const DELETE_CHANNEL_TEXT = {
  DISCLAIMER_TEXT: 'This cannot be undone.',
  ERROR_MESSAGE: 'Unable to delete subscription channel.',
  HEADER_TEXT: 'Delete subscription channel?',
  MODAL_ID: 'delete-channel-modal',
  SUBMIT_BUTTON_LABEL: 'Delete subscription channel',
  SUCCESS_MESSAGE: 'Success! Deleted:',
  VERIFY_TEXT: 'Are you sure you want to delete',
};

type Props = {
  channel: Channel;
  onClose: () => void;
  refetchChannels: () => void;
};

export const DeleteChannelModal = ({ channel, onClose, refetchChannels }: Props) => {
  const dispatch = useDispatch();

  const { isLoading, mutate } = useDeleteChannelMutation({
    onSuccess: () => {
      onClose();
      dispatch(
        addPositiveToast(
          <>
            {DELETE_CHANNEL_TEXT.SUCCESS_MESSAGE} <strong>{channel.name}</strong>
          </>,
        ),
      );
      refetchChannels();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;
      const errorMessage = response?.data?.message || '';
      onClose();
      dispatch(
        addNegativeToastWithErrorId(
          `${DELETE_CHANNEL_TEXT.ERROR_MESSAGE} ${errorMessage}`,
          'DeleteChannelModal',
          e,
        ),
      );
    },
  });

  const handleSubmit = () => {
    if (!channel.projectId || !channel.id) return null;
    const payload = {
      projectId: channel.projectId,
      messageChannelId: channel.id,
    };
    mutate(payload);
  };

  return (
    <Modal
      id={DELETE_CHANNEL_TEXT.MODAL_ID}
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader>{DELETE_CHANNEL_TEXT.HEADER_TEXT}</ModalHeader>
      <ModalBody>
        {DELETE_CHANNEL_TEXT.VERIFY_TEXT} <strong>{channel.name}</strong>
        {`? ${DELETE_CHANNEL_TEXT.DISCLAIMER_TEXT}`}
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <DestructiveButton
          data-test="delete-channel-btn"
          onClick={handleSubmit}
          isLoading={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {DELETE_CHANNEL_TEXT.SUBMIT_BUTTON_LABEL}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
